import * as React from "react";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "../../redux";
import { ClearingEventArgs, FileInfo, RemovingEventArgs, SelectedEventArgs, UploaderComponent, UploadingEventArgs } from "@syncfusion/ej2-react-inputs";
import { addUploadedFile, deleteFilesByOriginalName } from "../../redux/modules/file";
import "../../../../node_modules/@syncfusion/ej2-inputs/styles/uploader/fabric.css";
import "../../../../node_modules/@syncfusion/ej2-inputs/styles/input/fabric.css";
import "../../../../node_modules/@syncfusion/ej2-base/styles/fabric.css";
import "../../../../node_modules/@syncfusion/ej2-buttons/styles/fabric.css";
import { removeExtension } from "../helpers/stringHelper";
import { v4 as uuidv4 } from 'uuid';
import { IFileToUpload } from "../../interfaces/IFileToUpload";
import { logger } from "../../../../utilities/logger";
import authModule from "../../../../utilities/AuthModule";
import { apiHeaders } from "../../../../utilities/AxiosRetryUtil";

const mapStateToProps = (state: RootState) => {
  return {
    expanders: state.expanders,
    accountId: state.user?.account?.uniqueId
  };
};

const mapDispatchToProps = {
  addUploadedFile,
  deleteFilesByOriginalName
};
let localeLang: string = "";
const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
interface UploadFileProps extends PropsFromRedux {
  onComplete: (fails: number, successes: number, total: number) => void;
  onClear: () => void;
  enableAutoUpload?: boolean;
  allowMultipleFiles: boolean;
  allowedExtensions?: string;
  onFileUploaded?: (uploadedFile: IFileToUpload) => void;
}

const UploaderContainer: React.FunctionComponent<UploadFileProps> = props => {
  const accountUniqueId = React.useRef<string>(props.accountId);

  const [uploadObject, setUploadObject] = React.useState<UploaderComponent>(null);
  const [isInteraction, setIsInteraction] = React.useState<boolean>(false);
  const [asyncSettings, setAsyncSettings] = React.useState<object>(null);
  const [autoUpload, setAutoUpload] = React.useState<boolean>(props.enableAutoUpload ? props.enableAutoUpload : false);
  const [filesSuccess, setFilesSuccess] = React.useState<number>(0);
  const [filesFail, setFilesFail] = React.useState<number>(0);
  const [totalFiles, setTotalFiles] = React.useState<number>(0);
  const [allFiles, setAllFiles] = React.useState<FileInfo[]>([]);

  React.useEffect(() => {
    (async () => {
      if (!accountUniqueId.current) {
        accountUniqueId.current = (await authModule.getAccount(false))?.uniqueId;
      }

      await authModule.getDocubirdToken(true);

      setAsyncSettings({
        saveUrl: `${process.env.API_URL}Upload?accountId=${accountUniqueId.current}`,
        removeUrl: "",
        //chunkSize: 100000000 //100MB
      });
    })();

    try {
      localeLang = Office.context.displayLanguage?.toLowerCase().substring(0, 2);
      switch (localeLang) {
        case "en":
          localeLang = "en-US";
          break;
        case "nl":
          localeLang = "nl-NL";
          break;
        default:
          localeLang = "en-US";
          break;
      }
    } catch (error) {
      localeLang = "en-US";
    }
  }, []);

  React.useEffect(() => {
    if (totalFiles > 0 && (filesFail + filesSuccess === totalFiles)) {
      if (props.enableAutoUpload) {
        if (filesFail === 0) {
          setTimeout(() => {
            allFiles.forEach(file => {
              uploadObject.remove(file, null, true);
            });

            props.onComplete(filesFail, filesSuccess, totalFiles);
            setTotalFiles(0);
            setFilesFail(0);
            setFilesSuccess(0);
          }, 350);
        }
      } else {
        props.onComplete(filesFail, filesSuccess, totalFiles);
      }
    }
  }, [totalFiles, filesSuccess, filesFail]);

  const onUploading = (args: UploadingEventArgs) => {
    const jwt = authModule.getDocubirdTokenCached();
    (args.currentRequest as XMLHttpRequest).setRequestHeader('Authorization', `Bearer ${jwt}`);
    (args.currentRequest as XMLHttpRequest).setRequestHeader(apiHeaders.key.xDocubirdAuth, apiHeaders.value.xDocubirdAuth);
  }

  const onRemoveFile = (args: RemovingEventArgs): void => {
    try {
      args.postRawFile = false;
      if (props.onFileUploaded) {
        props.onFileUploaded(null);
      } else {
        //set redux
        props.deleteFilesByOriginalName(args.filesData?.map(fileInfo => fileInfo.name));
      }
      setTotalFiles(prevTotal => prevTotal > 0 ? --prevTotal : 0);
      args.filesData.map(fileInfo => fileInfo.statusCode === "0"
        ? setFilesFail(prevFail => prevFail > 0 ? --prevFail : 0)
        : setFilesSuccess(prevSuccess => prevSuccess > 0 ? --prevSuccess : 0)
      );
    } catch (error) {
      logger(error, "UploaderContainer.tsx (onRemoveFile)");
    }
  };

  const onPausing = (args: any): void => {
    setIsInteraction(args.event !== null && !navigator.onLine);
  };

  const onResuming = (args: any): void => {
    setIsInteraction(args.event !== null && !navigator.onLine);
  };

  const onClear = (args: ClearingEventArgs) => {
    if (!props.onFileUploaded) {
      //set redux
      props.deleteFilesByOriginalName(args.filesData?.map(fileInfo => fileInfo.name));
    }
    props.onClear();
    setTotalFiles(0);
    setFilesSuccess(0);
    setFilesFail(0);
  }

  const onFilesSelected = (args: SelectedEventArgs) => {
    if (!props.allowMultipleFiles && props.allowedExtensions && args.filesData[0].statusCode === '0') {
      return;
    }
    else {
      setAllFiles([...(allFiles ?? []), ...args.filesData]);
      setTotalFiles(prevTotal => prevTotal + args.filesData.length);
      const failsOnSelect = args.filesData.filter(file => file.statusCode === "0").length;
      setFilesFail(prevFail => prevFail + failsOnSelect);
    }
  }

  const onSuccess = (args: any) => {
    if (args.operation === "upload") {
      const name = removeExtension(args.file.name);

      const fileToUpload: IFileToUpload = {
        id: uuidv4(),
        ewsId: null,
        name: name,
        extension: args.file.type,
        isAttachment: false,
        isSelected: true,
        isDuplicate: false,
        originalName: name,
        lastModifiedDateTime: new Date((args.file.rawFile as File).lastModified).toISOString()
      }

      if (props.onFileUploaded) {
        //set parent local state
        props.onFileUploaded(fileToUpload);
      } else {
        //set redux
        props.addUploadedFile(fileToUpload);
        if (filesFail > 0 && (uploadObject?.getFilesData() ?? []).some(existingFile => existingFile.name === args.file.name)) {
          setFilesFail(prevFail => --prevFail);
        }
        setFilesSuccess(prevSuccess => ++prevSuccess);
      }
    }
  }

  const onFail = (args: any) => {
    if (args.operation === "upload") {
      setFilesFail((uploadObject?.getFilesData() ?? []).filter(fileInfo => fileInfo.statusCode === "0").length);
    }
  }

  return (
    <UploaderComponent
      id="chunkUpload"
      type="file"
      ref={scope => {
        setUploadObject(scope);
      }}
      locale={localeLang}
      maxFileSize={1024000000} //1 GB
      asyncSettings={asyncSettings}
      autoUpload={autoUpload}
      uploading={onUploading}
      removing={onRemoveFile}
      pausing={onPausing}
      resuming={onResuming}
      selected={onFilesSelected}
      success={onSuccess}
      failure={onFail}
      clearing={onClear}
      multiple={props.allowMultipleFiles}
      allowedExtensions={props.allowedExtensions}
    ></UploaderComponent >
  );
};

export default connector(UploaderContainer);
